import { Controller } from "@hotwired/stimulus"

// Screen 1 of the onboarding routine insights: plays the one-time "analyzing"
// intro overlay, counts the routine score up once it clears, and keeps the
// carousel counter + dots in sync as the user swipes. All motion is skipped
// under prefers-reduced-motion.
export default class extends Controller {
    static targets = ["intro", "introLine", "scoreNumber", "counter", "carousel", "dots", "card"]

    connect() {
        this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        this.introPlayed = false

        this.updatePosition()

        if (this.reduced) {
            this.finishIntro(false)
            return
        }

        this.playIntro()
    }

    playIntro() {
        if (this.introPlayed || !this.hasIntroTarget) return
        this.introPlayed = true

        this.setScoreNumber(0)

        const lineGap = 900
        this.introLineTargets.forEach((line, i) => {
            setTimeout(() => { line.style.opacity = "1" }, 300 + i * lineGap)
        })

        const total = 300 + this.introLineTargets.length * lineGap + 300
        setTimeout(() => this.finishIntro(true), total)
    }

    finishIntro(animate) {
        if (this.hasIntroTarget) {
            this.introTarget.style.opacity = "0"
            setTimeout(() => this.introTarget.classList.add("hidden"), animate ? 350 : 0)
        }

        setTimeout(() => this.countScoreUp(), animate ? 350 : 0)
    }

    countScoreUp() {
        if (!this.hasScoreNumberTarget) return

        const target = parseInt(this.scoreNumberTarget.dataset.finalScore, 10) || 0

        if (this.reduced) {
            this.setScoreNumber(target)
            return
        }

        const duration = 1000
        let start = null
        const step = (now) => {
            if (start === null) start = now
            const progress = Math.min((now - start) / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            this.setScoreNumber(Math.round(target * eased))
            if (progress < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
    }

    setScoreNumber(value) {
        if (this.hasScoreNumberTarget) this.scoreNumberTarget.textContent = value
    }

    updatePosition() {
        if (!this.hasCarouselTarget || this.cardTargets.length === 0) return

        const carouselRect = this.carouselTarget.getBoundingClientRect()
        const center = carouselRect.left + carouselRect.width / 2

        let closest = 0
        let min = Infinity
        this.cardTargets.forEach((card, index) => {
            const rect = card.getBoundingClientRect()
            const distance = Math.abs(rect.left + rect.width / 2 - center)
            if (distance < min) {
                min = distance
                closest = index
            }
        })

        this.setActive(closest)
    }

    setActive(index) {
        if (this.hasCounterTarget) {
            this.counterTarget.textContent = `${index + 1} of ${this.cardTargets.length}`
        }

        if (this.hasDotsTarget) {
            Array.from(this.dotsTarget.children).forEach((dot, i) => {
                dot.classList.toggle("bg-warm-gray-700", i === index)
                dot.classList.toggle("bg-warm-gray-300", i !== index)
            })
        }
    }
}
